import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams, useSearchParams, useNavigate } from 'react-router-dom'
import LegalPage from './pages/LegalPage'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { api, setToken } from './api/client'
import Layout from './components/Layout'
import LoginModal from './components/LoginModal'
import ProtectedRoute from './components/ProtectedRoute'
import { ToastProvider, ToastContainer } from './components/Toast'
import Landing from './pages/Landing'

// Route-level code splitting: pages loaded lazily
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Assets = lazy(() => import('./pages/Assets'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Search = lazy(() => import('./pages/Search'))
const Database = lazy(() => import('./pages/Database'))
const Insights = lazy(() => import('./pages/Insights'))
const Settings = lazy(() => import('./pages/Settings'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const VaultManagement = lazy(() => import('./pages/VaultManagement'))
const ShareManagement = lazy(() => import('./pages/ShareManagement'))
const KnowledgeGraphPage = lazy(() => import('./pages/KnowledgeGraphPage'))
const ReviewQueue = lazy(() => import('./pages/ReviewQueue'))

// ── Suspense fallback ───────────────────────────────
function PageLoader() {
  return (
    <div className="flex flex-col items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-line border-t-brand-600 rounded-full animate-spin mb-3" />
      <p className="text-sm text-ink-500">加载中...</p>
    </div>
  )
}

// ── WeChat OAuth callback ───────────────────────────
function WechatSuccess() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { loginWithToken } = useAuth()
  const [error, setError] = useState('')

  useEffect(() => {
    const token = searchParams.get('token')
    if (token) {
      loginWithToken(token)
        .then(() => navigate('/', { replace: true }))
        .catch(() => {
          // Fallback: set token directly and reload
          setToken(token)
          navigate('/', { replace: true })
        })
    } else {
      setError('缺少登录凭证，请重新扫码')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="text-brand-600 text-sm font-medium hover:text-brand-700"
          >
            返回首页
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-line border-t-brand-600 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-ink-500">正在登录，请稍候...</p>
      </div>
    </div>
  )
}

// ── Team invite acceptance form ─────────────────────
function InvitePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { loginWithToken } = useAuth()
  // 后端返回的邀请链接是路径式 /invite/<token>（Team.jsx 拿它直接拼 origin 复制给用户），
  // 而这里以前只注册 /invite 且从 query 取 token —— 受邀者点开链接必然落空，
  // 团队邀请整条路等于不通。两种形状都认。
  const { token: pathToken } = useParams()
  const token = pathToken || searchParams.get('token')

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  if (!token) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 text-sm mb-4">邀请链接无效</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="text-brand-600 text-sm font-medium hover:text-brand-700"
          >
            返回首页
          </button>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim()) { setError('请输入姓名'); return }
    if (password.length < 6) { setError('密码至少 6 个字符'); return }
    setError('')
    setLoading(true)
    try {
      const result = await api.acceptInvite(token, password, name.trim())
      if (result.access_token) {
        await loginWithToken(result.access_token)
        setSuccess(true)
        setTimeout(() => navigate('/', { replace: true }), 1500)
      } else {
        setSuccess(true)
        setTimeout(() => navigate('/', { replace: true }), 1500)
      }
    } catch (err) {
      setError(err.message || '接受邀请失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="flex items-center justify-center h-screen bg-ink-50">
        <div className="text-center animate-fade-up">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-ink-900 font-medium">已成功加入团队！</p>
          <p className="text-sm text-ink-500 mt-1">正在跳转...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center h-screen bg-ink-50">
      <div className="w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl animate-scale-in">
        <div className="p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-600 to-brand-700 flex items-center justify-center text-white font-bold text-lg mx-auto mb-4">
              T
            </div>
            <h2 className="text-lg font-semibold text-ink-900">团队邀请</h2>
            <p className="text-sm text-ink-500 mt-1">你被邀请加入团队</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-600">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1.5">姓名</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-line text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                placeholder="请输入姓名"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1.5">设置密码</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 pr-10 rounded-lg border border-line text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  placeholder="至少 6 个字符"
                  minLength={6}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-brand-700 text-white text-sm font-medium hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  处理中...
                </span>
              ) : (
                '接受邀请并注册'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

// ── Main app routes ─────────────────────────────────
function AppRoutes() {
  const { user, loading, showLogin, openLogin, closeLogin } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-2 border-line border-t-brand-600 rounded-full animate-spin" />
      </div>
    )
  }

  // WeChat callback and invite routes are accessible regardless of auth
  return (
    <Routes>
      <Route path="/wechat-success" element={<WechatSuccess />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="*" element={user ? <AuthenticatedRoutes /> : <UnauthenticatedRoutes />} />
    </Routes>
  )
}

function UnauthenticatedRoutes() {
  const { showLogin, openLogin, closeLogin } = useAuth()

  return (
    <>
      <Landing onOpenLogin={openLogin} />
      {showLogin && <LoginModal onClose={closeLogin} />}
    </>
  )
}

function AuthenticatedRoutes() {
  const { user } = useAuth()

  // If onboarding not completed, show onboarding page
  if (user && !user.onboarding_completed) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/assets" element={<Assets />} />
          <Route path="/search" element={<Search />} />
          <Route path="/database" element={<Database />} />
          <Route path="/knowledge-graph" element={<KnowledgeGraphPage />} />
          {/* 审阅动作需要 edit 权限；viewer 进来只会看到拒绝提示 */}
          <Route path="/review-queue" element={<ProtectedRoute requiredPermission="edit"><ReviewQueue /></ProtectedRoute>} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/settings/*" element={<ProtectedRoute requiredPermission="manage_settings"><Settings /></ProtectedRoute>} />
          <Route path="/shares" element={<ShareManagement />} />
          <Route path="/vault" element={<VaultManagement />} />
          {/* Legacy redirects */}
          <Route path="/storage" element={<Navigate to="/settings?tab=storage" replace />} />
          <Route path="/team" element={<Navigate to="/settings?tab=team" replace />} />
          <Route path="/pricing" element={<Navigate to="/settings?tab=pricing" replace />} />
          <Route path="/brand-profile" element={<Navigate to="/assets?tab=profile" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppRoutes />
        <ToastContainer />
      </AuthProvider>
    </ToastProvider>
  )
}
